<template>
  <div ref="reservationDetailsRoot" class="reservation-details-wrapper">
    <!-- Page Header -->
    <div class="page-header">
      <div class="page-header-top pdf-exclude">
        <button class="btn-retour" @click="goBack">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
          Retour
        </button>
      </div>
      <h1 class="page-title merriweather-reg-32">Confirmation de réservation</h1>
      <p class="reservation-meta raleway-medi-p18">
        NUMÉRO DE RÉSERVATION : <strong>{{ reservation.booking_reference || reservation.id }}</strong>
        &nbsp;|&nbsp;
        DATE DE RÉSERVATION : <strong>{{ formatDateShort(reservation.created_at || new Date()) }}</strong>
      </p>
      <p class="text-uppercase raleway-medi-p15 mt-2">STATUT DE RÉSERVATION : <span class="status-badge" :class="statusBadgeClass">{{ statusBadgeLabel }}</span></p>
    </div>

    <!-- Stay Summary Card -->
    <div class="stay-summary-card shadow-sm">
      <div class="stay-summary-inner">
        <!-- Left: Lodging Info -->
        <div class="lodging-summary">
          <div class="lodging-image-wrap" v-if="lodgingPhoto">
            <img :src="lodgingPhoto" alt="lodging" crossorigin="anonymous" class="lodging-thumb" />
          </div>
          <div class="lodging-info">
            <strong class="lodging-name merriweather-reg-18">{{ lodging.name }}</strong>
            <p class="lodging-travelers raleway-reg-p15">
              <span class="raleway-medi-p15">Nombre de voyageurs :</span> <span class="raleway-semi-p15">{{ reservation.adults }}</span> adultes - <span class="raleway-semi-p15">{{ reservation.children || 0 }}</span> enfants
            </p>
            <p class="lodging-address raleway-reg-p15">
              <span class="raleway-medi-p15">Adresse :</span> {{ lodgingAddress }}
            </p>
            <p class="lodging-maps raleway-semi-p15 text-underline cursor-pointer pdf-exclude" @click="displayMapfind">
              voir sur la carte
            </p>
          </div>
        </div>

        <!-- Right: Dates (individual cards) -->
        <div class="summary-dates">
          <div class="summary-item-card">
            <span class="label">ARRIVÉE</span>
            <span class="day-num">{{ formatDay(reservation.checkin) }}</span>
            <span class="month-year">{{ formatMonthYear(reservation.checkin) }}</span>
            <span class="sub-value">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#3B6152" stroke-width="2"><circle cx="12" cy="12" r="10"/><polyline points="9 12 11 14 15 10"/></svg>
              Check-in : <strong>{{ checkinHour }}</strong>
            </span>
          </div>
          <div class="summary-item-card">
            <span class="label">DÉPART</span>
            <span class="day-num">{{ formatDay(reservation.check_out) }}</span>
            <span class="month-year">{{ formatMonthYear(reservation.check_out) }}</span>
            <span class="sub-value">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><polyline points="9 12 11 14 15 10"/></svg>
              Check-out : <strong>{{ checkoutHour }}</strong>
            </span>
          </div>
          <div class="summary-item-card summary-item-card--nights">
            <span class="label">NUITÉES</span>
            <span class="day-num">{{ reservation.duration }}</span>
            <span class="month-year">nuit(s)</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Main Section: Votre réservation -->
    <section class="details-section">
      <h2 class="section-title merriweather-reg-24">Votre réservation</h2>
      <div class="reservation-card shadow-sm">
        <div class="card-content">
          <!-- Row 1: Room name + base price -->
          <div v-for="(roomLine, roomIndex) in reservationRoomBlocks" :key="`room-block-${roomIndex}`" class="room-block">
            <div class="card-row card-row-header">
              <div class="d-flex flex-column">
                <div class="room-info mb-1">
                  <span class="room-name raleway-semi-p15 text-uppercase">{{ roomLine.room_name || reservation.room_name }}</span>
                    <span class="capacity-icon">
                      {{ reservation.adults }} <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/></svg>
                    </span>
                  </div>
                  <div>
                    <!-- Row 2: Features line 1 -->
                    <div class="features-line mt-2 raleway-reg-p15" v-if="roomDetailsForLine(roomLine) && (roomDetailsForLine(roomLine).bed_type || roomDetailsForLine(roomLine).view_on || roomDetailsForLine(roomLine).area)">
                      <span class="feat-bullet">•</span>
                      <span v-if="roomDetailsForLine(roomLine).bed_type">{{ roomDetailsForLine(roomLine).bed_type }}</span>
                      <span v-if="roomDetailsForLine(roomLine) && (roomDetailsForLine(roomLine).view_on || roomDetailsForLine(roomLine).area)" class="feat-dot">·</span>
                      <span v-if="roomDetailsForLine(roomLine).view_on">{{ roomDetailsForLine(roomLine).view_on }}</span>
                      <span v-if="roomDetailsForLine(roomLine).area" class="feat-dot">·</span>
                      <span v-if="roomDetailsForLine(roomLine).area">Superficie {{ roomDetailsForLine(roomLine).area }} m²</span>
                    </div>

                    <!-- Row 3: Features line 2 -->
                    <div class="features-line raleway-reg-p15">
                      <span class="feat-bullet">•</span>
                      <span>{{ roomLine.board_type || reservation.board_type || 'Chambre seulement' }}</span>
                    </div>
                  </div>
              </div>
              <div class="base-price-box">
                <span class="price-val">{{ formatPrice(roomBasePrice(roomLine)) }} EUR</span>
                <span class="price-per-night mt-1">soit {{ pricePerNightFromBase(roomBasePrice(roomLine)) }} EUR</span>
                <span class="price-per-night">par nuit</span>
              </div>
            </div>
            <div v-if="roomIndex < reservationRoomBlocks.length - 1" class="card-section-divider"></div>
          </div>

          <!-- Reductions -->
          <div v-if="reservation.discount > 0" class="reductions-section">
            <div class="card-section-divider"></div>
            <span class="divider-label">RÉDUCTIONS & OFFRES</span>
            <div class="card-row">
              <span class="code-info">• Code appliqué : <span class="raleway-semi-p15">{{ reservation.code_name }}</span> <span class="badge-promo">-{{ discountPercent }}%</span></span>
              <span class="reduction-val">-{{ formatPrice(reservation.discount) }} EUR</span>
            </div>
          </div>

          <!-- Total -->
          <div class="total-to-pay-section">
            <div class="card-section-divider"></div>
            <div class="card-row card-row-total">
              <div class="total-left">
                <span class="divider-label" style="margin-bottom: 4px;">TOTAL À PAYER</span>
                <span class="payment-note raleway-reg-p15">Paiement à l'établissement le jour d'arrivée</span>
              </div>
              <span class="final-val">{{ formatPrice(reservation.total) }} EUR</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Payment Info -->
    <section class="details-section">
      <h2 class="section-title merriweather-reg-24">Information sur le paiement</h2>
      <div class="payment-info-content raleway-reg-p15">
        <ul>
          <li>• Vous avez confirmé et garanti votre réservation avec votre carte bancaire.</li>
          <li>• Les coordonnées de votre carte sont conservées en toute sécurité.</li>
          <li>• Le <strong>règlement de votre séjour</strong> se fera directement sur place, avec le moyen de paiement de votre choix.</li>
          <li>• Si un acompte est nécessaire, l'établissement prendra directement contact avec vous.</li>
          <li>• Cet hébergement accepte les modes de paiement suivants : <strong>{{ paymentMethods }}</strong></li>
        </ul>
      </div>
    </section>

    <!-- Additional Info -->
    <section class="details-section">
      <h2 class="section-title merriweather-reg-24">Informations supplémentaires</h2>
      <div class="extra-info-grid">
        <div class="info-block">
          <h3 class="info-subtitle"><span class="raleway-reg-p15">•</span> CONDITIONS D'ANNULATION</h3>
          <p class="raleway-reg-p15">{{ reservation.cancel_conditions || "En cas d'annulation ou de non présentation, le paiement intégral de la réservation sera dû." }}</p>
        </div>
        <div class="info-block">
          <h3 class="info-subtitle"><span class="raleway-reg-p15">•</span> DEMANDES SPÉCIALES</h3>
          <p class="raleway-reg-p15">{{ specialRequestsText || "Aucune demande spéciale." }}</p>
        </div>
      </div>
      <!-- Services supplémentaires -->
      <div v-if="parsedSupplements.length > 0" class="supplements-details-block mt-4">
        <h3 class="info-subtitle"><span class="raleway-reg-p15">•</span> SERVICES SUPPLÉMENTAIRES</h3>
        <div class="supplements-details-list">
          <div
            v-for="(supp, suppIndex) in parsedSupplements"
            :key="supp.label"
            :class="['supplements-details-row', { 'supplements-details-row--last': suppIndex === parsedSupplements.length - 1 }]"
          >
            <span class="raleway-reg-p15">{{ supp.label }} ×{{ supp.qty }}</span>
            <span class="raleway-semi-p15">{{ supp.price > 0 ? supp.price.toFixed(2) + ' €' : 'Gratuit' }}</span>
          </div>
          <div class="supplements-details-total">
            <span class="raleway-semi-p15">Total services additionnels</span>
            <span class="raleway-semi-p15">{{ supplementsTotal.toFixed(2) }} €</span>
          </div>
          <p class="raleway-it-l12 mt-1" style="color:#707070;">Ces services additionnels sont à régler directement sur place.</p>
        </div>
      </div>
    </section>

    <!-- Lodging Details Grid -->
    <section class="details-section">
      <h2 class="section-title merriweather-reg-24">Informations sur l'hébergement</h2>
      <div class="lodging-grid">
        <div class="grid-item">
          <span class="grid-label">• LANGUES PARLÉES</span>
          <span class="grid-val">{{ languages }}</span>
        </div>
        <div class="grid-item">
          <span class="grid-label">• ANIMAUX DE COMPAGNIE</span>
          <span class="grid-val">{{ animalPolicy }}</span>
        </div>
        <div class="grid-item">
          <span class="grid-label">• GARE À PROXIMITÉ</span>
          <span class="grid-val">{{ nearestStation }}</span>
        </div>
        <div class="grid-item">
          <span class="grid-label">• PLAGE À PROXIMITÉ</span>
          <span class="grid-val">{{ nearestBeach }}</span>
        </div>
        <div class="grid-item">
          <span class="grid-label">• ACCESSIBLE AUX PMR</span>
          <span class="grid-val">{{ pmrAccess }}</span>
        </div>
      </div>
    </section>

    <!-- Help Section -->
    <section class="details-section help-section">
      <div class="help-inner">
        <div class="help-left">
          <h2 class="section-title merriweather-reg-24">Besoin d'aide ?</h2>
          <p class="help-desc raleway-reg-p15">Notre équipe est à votre disposition pour toute question avant, pendant ou après votre séjour.</p>
        </div>
        <div class="help-right">
          <a href="mailto:customercare@hortense.green" class="contact-link">
            <span class="contact-icon-wrap">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
            </span>
            customercare@hortense.green
          </a>
          <a href="tel:+33626203846" class="contact-link">
            <span class="whatsapp-icon-custom">
              <i class="fa-brands fa-whatsapp"></i>
            </span>
            +33 (0) 6 26 20 38 46
          </a>
        </div>
      </div>
    </section>

    <!-- Vos Privilèges -->
    <section class="details-section privileges-section">
      <h2 class="section-title merriweather-reg-24">Vos privilèges</h2>
      <div class="privileges-content">
        <div class="privileges-left">
          <div class="privilege-block">
            <h3 class="info-subtitle"><span class="raleway-reg-p15">•</span> VOS POINTS DE FIDÉLITÉ</h3>
            <p class="raleway-reg-p15">Suite à votre réservation, <strong>{{ reservationPoints }} points de fidélité</strong> seront crédités sur votre compte Club Hortense après le check-out.</p>
          </div>
          <div v-if="reservation.duration > 3 && lodging.lodging_description && lodging.lodging_description.green_experience && lodging.lodging_description.green_experience != 'null' && lodging.lodging_description.green_experience != ''" class="privilege-block">
            <h3 class="info-subtitle"><span class="raleway-reg-p15">•</span> EXPÉRIENCE EXCLUSIVE LIÉE À VOTRE SÉJOUR</h3>
            <p  class="raleway-reg-p15">Vous avez réservé {{ reservation.duration }} nuits, Hortense vous offre durant votre séjour :</p>
            <div class="gift-list d-flex">
              <i class="fa-solid fa-gift text-green mt-1" style="margin-right: 5px; font-size: 17px;"></i>
              <span class="gift-item raleway-reg-p15">{{ lodging.lodging_description.green_experience }}</span>
            </div>
          </div>
          <div class="privilege-block">
            <h3 class="info-subtitle"><span class="raleway-reg-p15">•</span> {{exclusiveGifts && exclusiveGifts.length > 0 ? `Grace à votre statut ${reservation.current_loyalty_status}, vous avez le droit à ${exclusiveGifts.length} ${exclusiveGifts.length === 1 ? 'privilège offert' : 'privilèges offerts'} pendant votre séjour` : 'AUCUN PRIVILEGE EXCLUSIF POUR CE SEJOUR'}}</h3>
            <div v-if="exclusiveGifts && exclusiveGifts.length > 0" class="gift-list d-flex">
              <i class="fa-solid fa-gift text-green mt-1" style="margin-right: 5px; font-size: 17px;"></i>
              <span class="gift-item raleway-reg-p15">{{ exclusiveGifts.join(', ') }}</span>
            </div>
          </div>
        </div>

        <div class="privileges-right">
          <div class="loyalty-card shadow-sm">
            <p class="loyalty-header raleway-semi-p15 text-uppercase">Vos privilèges exclusifs et votre statut club Hortense</p>
            <p class="raleway-reg-p15">Votre statut Club Hortense vous donne accès à des privilèges exclusifs pendant votre séjour. Ces privilèges sont propres à chaque séjour.</p>
            <div class="loyalty-gauge-container">

              <!-- Gauge Grid -->
              <div class="gauge-scroll-wrapper">
                <div class="scroll-inner" :class="loyaltyUser.loyalty_points < 300 ? 'left-offset-margin' : ''">
                  <div class="gauge-grid">
                    <!-- Single continuous progress track -->
                    <div class="gauge-track">
                      <!-- Floating bubble (inside track for aligned positioning) -->
                      <div class="gauge-status-indicator" :style="gaugeStatusStyle">
                        <div class="bubble-content">
                          <div class="bubble-line">
                            <span class="bubble-label">Votre solde de points:</span>
                            <span class="bubble-val">{{ pointsBeforeReservation }} points</span>
                          </div>
                          <div class="bubble-line">
                            <span class="bubble-label">Votre nouveaux solde de points:</span>
                            <span class="bubble-val">{{ totalExpectedPoints }} points</span>
                          </div>
                        </div>
                        <div class="bubble-arrow"></div>
                      </div>
                      <div class="gauge-progress" :style="{ width: `${progressWidth}%` }"></div>
                      <!-- Checkpoints at 25%, 50%, 75%, 87.5% (center of each section) -->
                      <div class="gauge-checkpoint" style="left: 12.5%">
                        <i :class="totalExpectedPoints >= 500 ? 'fas fa-check' : 'fas fa-lock'"></i>
                      </div>
                      <div class="gauge-checkpoint" style="left: 37.5%">
                        <i :class="totalExpectedPoints >= 1000 ? 'fas fa-check' : 'fas fa-lock'"></i>
                      </div>
                      <div class="gauge-checkpoint" style="left: 62.5%">
                        <i :class="totalExpectedPoints >= 1500 ? 'fas fa-check' : 'fas fa-lock'"></i>
                      </div>
                      <div class="gauge-checkpoint" style="left: 87.5%">
                        <i :class="totalExpectedPoints >= 2500 ? 'fas fa-check' : 'fas fa-lock'"></i>
                      </div>
                    </div>

                    <!-- Labels row (4 columns) -->
                    <div class="gauge-labels-row">
                      <div class="gauge-section-label">
                        <div class="guage-label text-uppercase pt-3 text-center">{{ getDisplayGifts('green trotter').join(' - ') }}</div>
                        <div class="guage-sub-label pt-1">GREEN TROTTER</div>
                        <div class="gauge-sub-label pt-1">500 POINTS</div>
                      </div>
                      <div class="gauge-section-label">
                        <div class="guage-label text-uppercase pt-3 text-center">{{ getDisplayGifts('green trotter plus').join(' - ') }}</div>
                        <div class="guage-sub-label pt-1">GREEN TROTTER PLUS</div>
                        <div class="gauge-sub-label pt-1">1000 POINTS</div>
                      </div>
                      <div class="gauge-section-label">
                        <div class="guage-label text-uppercase pt-3 text-center">{{ getDisplayGifts('green trotter premium').join(' - ') }}</div>
                        <div class="guage-sub-label pt-1">GREEN TROTTER PREMIUM</div>
                        <div class="gauge-sub-label pt-1">1500 POINTS</div>
                      </div>
                      <div class="gauge-section-label">
                        <div class="guage-label text-uppercase pt-3 text-center">{{ getDisplayGifts('green trotter infinite').join(' - ') }}</div>
                        <div class="guage-sub-label pt-1">GREEN TROTTER INFINITE</div>
                        <div class="gauge-sub-label pt-1">2500 POINTS</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Map Modal -->
      <portal to="modals">
        <Maplodgingshow v-if="showMapfind" :markers="markers" :selected-lodging="lodging" :nearby-lodgings="nearbyLodgings" @close="showMapfind = false"></Maplodgingshow>
      </portal>
      
      <div class="confirmation-footer raleway-reg-p15 pdf-exclude">
        <p>Une confirmation de réservation vous a été envoyée par e-mail.</p>
        <p>Vous souhaitez télécharger cette confirmation de réservation ? <a href="#" class="btn-link" @click.prevent="downloadReservationPdf">Cliquez ici.</a></p>
        <p v-if="isGeneratingPdf" class="pdf-status-msg">Génération du PDF en cours…</p>
        <p v-if="pdfErrorMessage" class="pdf-error-msg">{{ pdfErrorMessage }}</p>
      </div>
    </section>
  </div>
</template>

<script>
import fetcher from 'packs/shared/api.js';
import html2pdf from 'html2pdf.js/dist/html2pdf.bundle';
import Maplodgingshow from '../../pages/lodgings/Maplodgingshow.vue';

export default {
  name: 'ReservationDetails',
  components: {
    Maplodgingshow
  },
  props: {
    reservation: {
      type: Object,
      required: true
    },
    user: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      loyaltyUser: {
        loyalty_points: this.user.loyalty_points || 0
      },
      showMapfind: false,
      nearbyLodgings: [],
      markers: [],
      isGeneratingPdf: false,
      pdfErrorMessage: ''
    };
  },
  mounted() {
    this.fetchNearbyLodgings(this.lodging.id);
  },  
  computed: {
    lodging() {
      return this.reservation.lodging || {};
    },
    room() {
      return this.reservation.room || {};
    },
    profile() {
      return this.lodging.lodging_profile || {};
    },
    checkinHour() {
      return this.profile.checkin_hour ? this.profile.checkin_hour.substring(0, 5) : '15:00';
    },
    checkoutHour() {
      return this.profile.checkout_hour ? this.profile.checkout_hour.substring(0, 5) : '11:00';
    },
    lodgingPhoto() {
      return this.lodging.photo_banner;
    },
    lodgingAddress() {
      const p = this.reservation.lodging;
      if (!p) return '';
      return p.full_address;
    },
    mapsCoords() {
      if (this.lodging.latitude && this.prolodgingfile.longitude) {
        return `${this.lodging.latitude},${this.lodging.longitude}`;
      }
      return null;
    },
    formatCancelDate() {
      // Mock logic for "before [date]" - usually 7 days before checkin
      if (!this.reservation.checkin) return '...';
      const checkin = new Date(this.reservation.checkin);
      checkin.setDate(checkin.getDate() - 7);
      return this.formatDate(checkin);
    },
    basePrice() {
      if (this.reservation.discount > 0) {
        return (this.reservation.total || 0) + (this.reservation.discount || 0);
      }
      return this.reservation.total || 0;
    },
    pricePerNight() {
      if (!this.basePrice || !this.reservation.duration) return '0';
      return (this.basePrice / this.reservation.duration).toFixed(1);
    },
    reservationRoomBlocks() {
      const lines = Array.isArray(this.reservation.room_lines) ? this.reservation.room_lines : [];
      if (lines.length > 0) return lines;

      return [{
        room_code: this.reservation.room_code || null,
        room_name: this.reservation.room_name || null,
        board_type: this.reservation.board_type || null,
        total_ttc: this.reservation.total || 0,
      }];
    },
    discountPercent() {
      // If code is BIENVENUE, show 10%
      if (this.reservation.code_name === 'BIENVENUE') return '10';
      // Calculate from total and discount if possible
      if (this.reservation.discount && this.reservation.total) {
        const fullPrice = this.reservation.total + this.reservation.discount;
        return Math.round((this.reservation.discount / fullPrice) * 100);
      }
      return '10';
    },
    paymentMethods() {
      if (this.lodging.payments && this.lodging.payments.length > 0) {
        return this.lodging.payments.map(p => p.name).join(', ');
      }
      return 'Espèces, Mastercard, Visa';
    },
    languages() {
      if (this.lodging.languages && this.lodging.languages.length > 0) {
        return this.lodging.languages.map(l => l.name).join(', ');
      }
      return 'Français, Anglais';
    },
    animalPolicy() {
      return this.profile.animal ? 'Admis' : 'Non admis';
    },
    pmrAccess() {
      return this.profile.pmr_access ? 'Oui' : 'Non';
    },
    nearestStation() {
      return this.profile.nearest_station || 'Non communiquée';
    },
    nearestBeach() {
      return this.profile.nearest_beach || 'Non communiquée';
    },
    reservationPoints() {
      return this.reservation.duration ? this.reservation.duration * 50 : 100;
    },
    pointsBeforeReservation() {
      const loyaltyPoints = parseInt(this.loyaltyUser.loyalty_points) || 0;
      return Math.max(0, loyaltyPoints - this.reservationPoints);
    },
    totalExpectedPoints() {
      const basePoints = parseInt(this.loyaltyUser.loyalty_points);
      const reservationPointsToAdd = 0; //this.reservation.past ? 0 : parseInt(this.reservationPoints);
      return basePoints + reservationPointsToAdd;
    },
    progressWidth() {
      const pts = this.totalExpectedPoints;
      const checkpoints = [500, 1000, 1500, 2500];
      const centers = [12.5, 37.5, 62.5, 87.5];
      const capped = Math.min(pts, 2500);
      let pos = 0;
      if (capped <= checkpoints[0]) {
        pos = (capped / checkpoints[0]) * centers[0];
      } else if (capped <= checkpoints[1]) {
        const p = (capped - checkpoints[0]) / (checkpoints[1] - checkpoints[0]);
        pos = centers[0] + p * (centers[1] - centers[0]);
      } else if (capped <= checkpoints[2]) {
        const p = (capped - checkpoints[1]) / (checkpoints[2] - checkpoints[1]);
        pos = centers[1] + p * (centers[2] - centers[1]);
      } else {
        const p = (capped - checkpoints[2]) / (checkpoints[3] - checkpoints[2]);
        pos = centers[2] + p * (centers[3] - centers[2]);
      }
      return pos;
    },
    gaugeStatusStyle() {
      const pts = this.totalExpectedPoints;
      const checkpoints = [500, 1000, 1500, 2500];
      const centers = [12.5, 37.5, 62.5, 87.5];
      const capped = Math.min(pts, 2500);
      let pos = 0;
      if (capped <= checkpoints[0]) {
        pos = (capped / checkpoints[0]) * centers[0];
      } else if (capped <= checkpoints[1]) {
        const p = (capped - checkpoints[0]) / (checkpoints[1] - checkpoints[0]);
        pos = centers[0] + p * (centers[1] - centers[0]);
      } else if (capped <= checkpoints[2]) {
        const p = (capped - checkpoints[1]) / (checkpoints[2] - checkpoints[1]);
        pos = centers[1] + p * (centers[2] - centers[1]);
      } else {
        const p = (capped - checkpoints[2]) / (checkpoints[3] - checkpoints[2]);
        pos = centers[2] + p * (centers[3] - centers[2]);
      }
      return { left: `${pos}%`, transform: 'translateX(-50%)' };
    },
    exclusiveGifts() {
      return this.reservation.service_exclusive || [];
    },
    statusBadgeClass() {
      if (this.reservation.cancel) return 'badge-cancelled';
      const today = this.dateNow();
      if (this.reservation.checkin >= today || this.reservation.check_out > today) return 'badge-upcoming';
      return 'badge-past';
    },
    statusBadgeLabel() {
      if (this.reservation.cancel) return 'ANNULÉE';
      const today = this.dateNow();
      if (this.reservation.checkin >= today || this.reservation.check_out > today) return 'À VENIR';
      return 'PASSÉE';
    },
    // Parse supplements from special_requests text (appended by ReservationNewUserInfo)
    specialRequestsText() {
      const raw = this.reservation.special_requests || '';
      const idx = raw.indexOf('\n\nServices supplémentaires sélectionnés :');
      return idx > -1 ? raw.substring(0, idx).trim() : raw.trim();
    },
    parsedSupplements() {
      const raw = this.reservation.special_requests || '';
      const marker = '\n\nServices supplémentaires sélectionnés :\n';
      const idx = raw.indexOf(marker);
      if (idx === -1) return [];
      const block = raw.substring(idx + marker.length);
      const lines = block.split('\n').filter(l => l && !l.startsWith('Total services'));
      return lines.map(line => {
        // Format: "Label ×qty (price €)" or "Label ×qty (Gratuit)"
        const match = line.match(/^(.+?) ×(\d+) \((.+?)\)$/);
        if (!match) return null;
        const label = match[1].trim();
        const qty = parseInt(match[2]) || 1;
        const priceStr = match[3].trim();
        const price = priceStr === 'Gratuit' ? 0 : parseFloat(priceStr.replace(' €', '')) || 0;
        return { label, qty, price };
      }).filter(Boolean);
    },
    supplementsTotal() {
      return this.parsedSupplements.reduce((sum, s) => sum + s.price, 0);
    }
  },
  created() {
    this.fetchLoyaltyUser();
  },
  methods: {
    goBack() {
      this.$emit('back');
    },
    dateNow() {
      const set = new Date();
      const d = set.getDate().toString().padStart(2, '0');
      const m = (set.getMonth() + 1).toString().padStart(2, '0');
      return `${set.getFullYear()}-${m}-${d}`;
    },
    formatDate(dateStr) {
      if (!dateStr) return '';
      const date = new Date(dateStr);
      return date.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    },
    formatDateShort(dateStr) {
      if (!dateStr) return '';
      const date = new Date(dateStr);
      return date.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    },
    formatDay(dateStr) {
      if (!dateStr) return '';
      return new Date(dateStr).getDate();
    },
    formatMonthYear(dateStr) {
      if (!dateStr) return '';
      const date = new Date(dateStr);
      return date.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
    },
    formatPrice(price) {
      if (price === undefined || price === null) return '—';
      return new Intl.NumberFormat('fr-FR', {
        maximumFractionDigits: 2
      }).format(price);
    },
    toNumber(value) {
      if (value === undefined || value === null || value === '') return 0;
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : 0;
    },
    roomDetailsForLine(roomLine) {
      const rooms = Array.isArray(this.lodging.rooms) ? this.lodging.rooms : [];
      if (roomLine?.room_code && rooms.length > 0) {
        const matched = rooms.find((r) => r.code === roomLine.room_code);
        if (matched) return matched;
      }

      return this.room || {};
    },
    roomBasePrice(roomLine) {
      if (this.reservationRoomBlocks.length === 1) {
        return this.basePrice;
      }

      const totalTtc = this.toNumber(roomLine?.total_ttc);
      if (totalTtc > 0) return totalTtc;

      const totalHt = this.toNumber(roomLine?.total_ht);
      const totalTax = this.toNumber(roomLine?.total_tax);
      const totalTva = this.toNumber(roomLine?.total_tva);
      return totalHt + totalTax + totalTva;
    },
    pricePerNightFromBase(basePrice) {
      const nights = this.toNumber(this.reservation.duration);
      if (!basePrice || !nights) return '0';
      return (this.toNumber(basePrice) / nights).toFixed(1);
    },
    async fetchLoyaltyUser() {
      try {
        const response = await fetcher.get(`/loyalty_status/${this.user.id}`);
        this.loyaltyUser = response.data;
      } catch (e) {
        this.loyaltyUser = { loyalty_points: this.user.loyalty_points || 0 };
      }
    },
    fetchGifts(trotterId) {
      const normalizeName = name =>
        name.trim().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
      const greenTrotters = this.lodging.green_trotters || [];
      const selectedTrotter = greenTrotters.find(gt => gt.name === trotterId);
      if (!selectedTrotter) return [];
      const allGifts = Array.from(
        new Map(
          greenTrotters
            .flatMap(gt => gt.gifts || [])
            .map(gift => [normalizeName(gift.name), gift])
        ).values()
      );
      const currentGifts = selectedTrotter.gifts || [];
      return allGifts.map((gift) => ({
        name: gift.name,
        active: currentGifts.some((g) => g.name === gift.name)
      }));
    },
    getDisplayGifts(statusId) {
      const gifts = this.fetchGifts(statusId);
      return gifts?.filter(g => g.active).map(g => g.name)?.slice(-1) || [];
    },
    displayMapfind() {
      this.markers = JSON.parse(JSON.stringify(this.markerMap()));
      this.showMapfind = !this.showMapfind;
    },
    markerMap() {
      const markers = [];
      if (this.lodging && this.lodging.longitude && this.lodging.latitude) {
        markers.push([this.lodging.longitude, this.lodging.latitude]);
      }
      return markers;
    },
    async fetchNearbyLodgings(id) {
      try {
        const response = await fetcher.get(
          `/lodgings/${this.lodging.id}/nearby`
        );
        console.log('response.data.nearby', response.data.nearby)
        console.log('response.data', response.data)
        this.nearbyLodgings = response.data.nearby;
      } catch (e) {
        console.error(e);
      }
    },
    isCrossOriginUrl(url) {
      if (!url) return false;
      if (url.startsWith('data:') || url.startsWith('blob:')) return false;
      try {
        const parsed = new URL(url, window.location.origin);
        return parsed.origin !== window.location.origin;
      } catch (e) {
        return false;
      }
    },
    async inlineImageAsDataUrl(img) {
      const src = img.getAttribute('src');
      if (!src || src.startsWith('data:') || src.startsWith('blob:')) return;

      try {
        const absoluteSrc = new URL(src, window.location.origin).href;
        const response = await fetch(absoluteSrc, { mode: 'cors', credentials: 'omit' });
        if (!response.ok) return;
        const blob = await response.blob();

        const dataUrl = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });

        if (typeof dataUrl === 'string') {
          img.setAttribute('src', dataUrl);
          img.removeAttribute('srcset');
        }
      } catch (e) {
        img.setAttribute('crossorigin', 'anonymous');
      }
    },
    async inlineImagesForPdf(node) {
      const images = Array.from(node.querySelectorAll('img'));
      await Promise.all(images.map((img) => this.inlineImageAsDataUrl(img)));
    },
    wait(ms = 0) {
      return new Promise((resolve) => setTimeout(resolve, ms));
    },
    waitForNextPaint() {
      return new Promise((resolve) => {
        requestAnimationFrame(() => requestAnimationFrame(resolve));
      });
    },
    async waitForFontsReady(timeoutMs = 4000) {
      if (!document.fonts || !document.fonts.ready) return;
      await Promise.race([
        document.fonts.ready.catch(() => undefined),
        this.wait(timeoutMs)
      ]);
    },
    waitForImageLoad(img, timeoutMs = 10000) {
      if (!img) return Promise.resolve();
      if (img.complete && img.naturalWidth > 0) return Promise.resolve();

      return new Promise((resolve) => {
        let settled = false;
        const done = () => {
          if (settled) return;
          settled = true;
          img.removeEventListener('load', done);
          img.removeEventListener('error', done);
          resolve();
        };

        img.addEventListener('load', done, { once: true });
        img.addEventListener('error', done, { once: true });
        setTimeout(done, timeoutMs);
      });
    },
    async waitForImagesToRender(node) {
      const images = Array.from(node.querySelectorAll('img'));
      await Promise.all(images.map((img) => this.waitForImageLoad(img)));
    },
    async ensurePdfNodeReady(node) {
      await this.$nextTick();
      await this.waitForNextPaint();
      await this.waitForFontsReady();
      await this.waitForImagesToRender(node);
      await this.waitForNextPaint();
    },
    isMobilePdfDevice() {
      const ua = (window.navigator.userAgent || '').toLowerCase();
      return /iphone|ipad|ipod|android|mobile/.test(ua) || window.matchMedia('(max-width: 1024px)').matches;
    },
    computePdfScale(contentWidth, contentHeight) {
      const isMobile = this.isMobilePdfDevice();
      const baseScale = isMobile ? 1.15 : 1.8;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const preferredScale = Math.min(baseScale, dpr);
      const maxPixels = isMobile ? 5_000_000 : 12_000_000;
      const safeAreaScale = Math.sqrt(maxPixels / Math.max(contentWidth * contentHeight, 1));
      const bounded = Math.min(preferredScale, Number.isFinite(safeAreaScale) ? safeAreaScale : preferredScale);
      return Math.max(0.7, bounded);
    },
    isCanvasEffectivelyBlank(canvas) {
      if (!canvas || !canvas.width || !canvas.height) return true;
      const ctx = canvas.getContext('2d', { willReadFrequently: true });
      if (!ctx) return true;

      const sampleSize = 24;
      const stepX = Math.max(1, Math.floor(canvas.width / sampleSize));
      const stepY = Math.max(1, Math.floor(canvas.height / sampleSize));

      for (let y = 0; y < canvas.height; y += stepY) {
        for (let x = 0; x < canvas.width; x += stepX) {
          const pixel = ctx.getImageData(x, y, 1, 1).data;
          const alpha = pixel[3];
          const isNearWhite = pixel[0] > 245 && pixel[1] > 245 && pixel[2] > 245;
          if (alpha > 0 && !isNearWhite) return false;
        }
      }

      return true;
    },
    createPdfClone(node) {
      // Create an isolated full-page overlay so html2canvas captures from x:0
      const wrapper = document.createElement('div');
      wrapper.style.cssText = [
        'position:fixed',
        'left:0',
        'top:0',
        'width:100vw',
        'min-height:100vh',
        'z-index:-9999',
        'opacity:0',
        'pointer-events:none',
        'background:#ffffff',
        'display:flex',
        'justify-content:flex-start',  // align clone to the LEFT edge
        'align-items:flex-start',
      ].join(';');

      const clone = node.cloneNode(true);
      clone.classList.add('pdf-mode');

      clone.style.cssText = [
        'position:relative',
        'width:1200px',               // fixed known width — no auto margins
        'max-width:none',
        'margin:0',                  // NO auto margin — must start at left:0
        'padding:48px 48px 64px 48px', // generous padding on all sides incl. top
        'transform:none',
        'box-sizing:border-box',
        'border-radius:0',
        'box-shadow:none',
        'background:#F7F7F7',
        'overflow:visible',
        'flex-shrink:0',
      ].join(';');

      clone.querySelectorAll('.pdf-exclude').forEach(el => el.remove());
      clone.querySelectorAll('img').forEach(img => {
        img.setAttribute('crossorigin', 'anonymous');
        img.removeAttribute('loading');
        img.removeAttribute('srcset');
      });

      wrapper.appendChild(clone);
      document.body.appendChild(wrapper);
      return { wrapper, clone };
    },

    async generatePdfFromNode(node, filename) {
      const { wrapper, clone } = this.createPdfClone(node);

      try {
        await this.ensurePdfNodeReady(clone);
        await this.inlineImagesForPdf(clone);
        await this.waitForImagesToRender(clone);
        await this.waitForNextPaint();
        // Let browser fully paint the clone at its fixed position
        await this.wait(200);

        const contentWidth  = Math.ceil(clone.offsetWidth  || 1200);
        const contentHeight = Math.ceil(clone.scrollHeight || clone.offsetHeight);

        const scale = Math.min(2, window.devicePixelRatio || 1);

        const options = {
          margin:   0,
          filename,
          image:    { type: 'jpeg', quality: 0.98 },
          html2canvas: {
            scale,
            useCORS:         true,
            allowTaint:      false,
            imageTimeout:    30000,
            backgroundColor: '#F7F7F7',
            logging:         false,
            // Critical: capture from x:0, y:0 of the clone element itself
            scrollX:         0,
            scrollY:         0,
            x:               0,        // ← no horizontal offset
            y:               0,
            width:           contentWidth,
            height:          contentHeight,
            windowWidth:     contentWidth,
            windowHeight:    contentHeight,
          },
          jsPDF: {
            unit:        'pt',
            // Convert px → pt (1px = 0.75pt at 96dpi)
            format:      [contentWidth * 0.75, contentHeight * 0.75],
            orientation: 'portrait',
          },
          pagebreak: { mode: [] },   // single continuous page — no breaks
        };

        await html2pdf().set(options).from(clone).save();

      } finally {
        if (wrapper?.parentNode) wrapper.parentNode.removeChild(wrapper);
      }
    },
    async downloadReservationPdf() {
      const printableNode = this.$refs.reservationDetailsRoot;
      if (!printableNode || this.isGeneratingPdf) return;

      const bookingReference = this.reservation.booking_reference || this.reservation.id || 'reservation';
      const filename = `confirmation-reservation-${bookingReference}.pdf`;

      this.isGeneratingPdf = true;
      this.pdfErrorMessage = '';

      try {
        await this.ensurePdfNodeReady(printableNode);
        await this.generatePdfFromNode(printableNode, filename);
      } catch (error) {
        console.error('PDF generation failed', error);
        this.pdfErrorMessage = 'Le téléchargement du PDF a échoué. Merci de réessayer dans quelques instants.';
        if (typeof window !== 'undefined' && typeof window.alert === 'function') {
          window.alert(this.pdfErrorMessage);
        }
      } finally {
        this.isGeneratingPdf = false;
      }
    },
  }
};
</script>

<style scoped>
.reservation-details-wrapper {
  padding: 40px 20px;
  max-width: 90%;
  margin: 0 auto;
  color: #1a1a1a;
  font-family: 'Raleway', sans-serif;
  background: #F7F7F7 0% 0% no-repeat padding-box;
  box-shadow: 0px 3px 6px #00000029;
  border-radius: 15px;

  @media screen and (max-width: 500px) {
    margin-top: 9rem;
  }
}

/* Page Header */
.page-header {
  text-align: center;
  margin-bottom: 40px;
}
.page-header-top {
  display: flex;
  justify-content: flex-start;
  margin-bottom: 16px;
}
.btn-retour {
  display: flex;
  align-items: center;
  gap: 8px;
  background: none;
  border: none;
  color: #000;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  text-decoration: underline;
  padding: 0;
  transition: opacity 0.2s;
}
.btn-retour:hover {
  opacity: 0.8;
}
.page-title {
  margin: 0 0 8px 0;
  color: #1a150e;
  font-size: 28px;
}
.reservation-meta {
  color: #000;
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

/* Stay Summary Card */
.stay-summary-card {
  background: #fff;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 40px;
  border: 1px solid #eee;
}
.stay-summary-inner {
  display: flex;
  align-items: flex-start;
  gap: 30px;
}

/* Lodging info left side */
.lodging-summary {
  display: flex;
  gap: 16px;
  flex: 1;
  align-items: flex-start;
}
.lodging-image-wrap {
  flex-shrink: 0;
  width: 131px;
  height: 126px;
  border-radius: 8px;
  overflow: hidden;
}
.lodging-thumb {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.lodging-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.lodging-name {
  display: block;
  color: #000;
  margin-bottom: 4px;
}
.lodging-travelers,
.lodging-address,
.lodging-maps {
  margin: 0;
}

.lodging-maps { 
  color: #3B6152;
}
.maps-link {
  color: #1a4d46;
  text-decoration: underline;
  font-size: 13px;
}

/* Right: Dates (individual cards) */
.summary-dates {
  display: flex;
  align-items: stretch;
  flex-shrink: 0;
  gap: 10px;
}
.summary-item-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 16px 22px;
  min-width: 100px;
  text-align: center;
  background: #FFFFFF 0% 0% no-repeat padding-box;
  box-shadow: 0px 3px 6px #00000029;
  border-radius: 22px;
  justify-content: flex-start;
}
.summary-item-card--nights {
  justify-content: flex-start;
}
.summary-item-card .label {
  font-family: 'Raleway', sans-serif;
  font-size: 15px;
  font-weight: 500;
  color: #3B6152;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin-bottom: 10px;
  border-bottom: 1px solid #E5DFDF;
  width: 100%;
  order: -1;
}
.day-num {
  font-size: 20px;
  font-weight: 600;
  line-height: 1;
  color: #000;
}
.month-year {
  font-size: 15px;
  color: #000;
  margin-bottom: 6px;
}
.summary-item-card .sub-value {
  display: flex;
  align-items: center;
  gap: 4px;
  font-weight: 600;
  font-size: 13px;
  color: #000;
  white-space: nowrap;
}

/* Sections */
.details-section {
  margin-bottom: 40px;
}
.section-title {
  margin-bottom: 20px;
  border-top: 1px solid #fff;
  padding-top: 20px;
  color: #000;
}

/* Reservation Card */
.reservation-card {
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid #eee;
}
.room-info {
  display: flex;
  align-items: center;
  gap: 8px;
}
.room-name { color: #1a150e; font-size: 14px; font-weight: 700; }
.capacity-icon {
  display: flex;
  align-items: center;
  gap: 3px;
  font-size: 15px;
  color: #000;
}

.card-content {
  padding: 20px 24px;
}

/* Card rows */
.card-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}
.card-row-header {
  margin-bottom: 8px;
}
.card-row-total {
  align-items: flex-start;
}

/* Base price box top-right */
.base-price-box {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  flex-shrink: 0;
  margin-left: 20px;
}
.price-val {
  font-weight: 600;
  font-size: 15px;
  color: #000;
}
.price-per-night {
  font-size: 15px;
  color: #000;
  line-height: 1.4;
}

/* Feature lines */
.features-line {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 4px;
  color: #444;
  margin-bottom: 4px;
}
.feat-bullet {
  margin-right: 2px;
  color: #444;
}
.feat-dot {
  color: #888;
  margin: 0 2px;
}

/* Section dividers inside card */
.card-section-divider {
  border-top: 1px solid #ebebeb;
  margin: 16px 0;
}

.divider-label {
  display: block;
  font-size: 15px;
  font-weight: 600;
  color: #000;
  margin-bottom: 10px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.reductions-section { }
.code-info { font-size: 15px; color: #000; }
.badge-promo {
  background: #e6f4f1;
  color: #3B6152;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 700;
  margin-left: 6px;
}
.reduction-val { color: #3B6152; font-weight: 600; font-size: 15px; flex-shrink: 0; margin-left: 20px; }

.total-to-pay-section { }
.total-left {
  display: flex;
  flex-direction: column;
}
.payment-note { font-size: 15px; color: #000; }
.final-val {
  font-size: 15px;
  font-weight: 600;
  color: #000;
  flex-shrink: 0;
  margin-left: 20px;
}

/* Payment Info Content */
.payment-info-content ul { list-style: none; padding: 0; color: #000; }
.payment-info-content li { margin-bottom: 10px; font-size: 15px; line-height: 1.5; }

/* Extra Info & Lodging Grid */
.extra-info-grid, .lodging-grid {
  display: grid;
  grid-template-rows: 1fr 1fr;
  gap: 24px 40px;
}

.extra-info-grid {
  gap: 5px;
}

.lodging-grid {
  grid-template-columns: 1fr 1fr;
}
.info-block { margin-bottom: 8px; }
.info-subtitle {
  font-size: 15px;
  font-weight: 600;
  margin-bottom: 6px;
  color: #000;
  text-transform: uppercase;
  
}
.grid-item {
  display: flex;
  flex-direction: column;
}
.grid-label {
  font-size: 15px;
  font-weight: 600;
  margin-bottom: 5px;
  color: #000;
  text-transform: uppercase;
  
}
.grid-val { color: #000; font-size: 15px; }

/* Help Section */
.help-section {
  border-top: 1px solid #eee;
  border-bottom: 1px solid #eee;
  padding: 30px 0;
}
.help-inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 30px;
}
.help-left { flex: 1; }
.help-left .section-title {
  border-bottom: none;
  padding-bottom: 0;
  margin-bottom: 8px;
}
.help-desc { margin: 0; font-size: 15px; color: #000; }
.help-right {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-shrink: 0;
  background: #f5f5f5;
  border-radius: 50px;
  padding: 10px 20px;
}
.contact-link {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 15px;
  color: #000;
  text-decoration: none;
  font-weight: 500;

  @media screen and (max-width: 500px) {
    width: 100%;
  }
}
.contact-link:hover { text-decoration: underline; }
.contact-icon-wrap {
  width: 28px;
  height: 28px;
  background: var(---3b6152) 0% 0% no-repeat padding-box;
  background: #3B6152 0% 0% no-repeat padding-box;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  flex-shrink: 0;
}

.whatsapp-icon-custom {
  font-size: 28px;
  color: #3B6152;
}

/* Privileges Section */
.privileges-content {
  display: grid;
  grid-template-columns: 1fr 1.7fr;
  gap: 40px;
  align-items: start;
}
.privilege-block { margin-bottom: 30px; }
.gift-list { margin-top: 15px; }
.gift-item {
  display: flex;
  align-items: center;
  gap: 10px;
  color: #3B6152;
  margin-bottom: 10px;
}
.gift-none { color: #999; font-style: italic; font-size: 13px; }

.privileges-left { min-width: 0; }
.privileges-right {
  min-width: 0;
  max-width: 100%;
}

.loyalty-card {
  background: #fff;
  border-radius: 12px;
  padding: 24px;
  border: 1px solid #eee;
}
.loyalty-header {
  color: #3B6152;
  margin-bottom: 20px;
  
  text-transform: uppercase;
}

/* Loyalty Gauge Container */
.loyalty-gauge-container {
  position: relative;
  padding-top: 10px;
}

/* Status indicator / floating bubble */
.gauge-status-indicator {
  position: absolute;
  top: -60px;
  transition: left 0.5s ease-out;
  transform: translateX(-50%);
  z-index: 2;
}

.bubble-content {
  background: #fff;
  border: 1px solid #2C5E4B;
  padding: 8px 14px;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  min-width: 140px;
  box-shadow: 0 4px 10px rgba(0,0,0,0.1);
}
.bubble-line {
  display: flex;
  gap: 4px;
  align-items: center;
  margin-bottom: 4px;
  white-space: nowrap;
}
.bubble-line:last-child {
  margin-bottom: 0;
}
.bubble-label { font-size: 9px; color: #2C5E4B; text-transform: uppercase; letter-spacing: 0.04em; font-weight: 600; }
.bubble-val { font-size: 9px; font-weight: 500; color: #000000; }
.bubble-arrow {
  width: 0;
  height: 0;
  border-left: 7px solid transparent;
  border-right: 7px solid transparent;
  border-top: 7px solid #2C5E4B;
  margin: 0 auto;
  position: relative;
}
.bubble-arrow::after {
  content: '';
  position: absolute;
  top: -9px;
  left: -6px;
  width: 0;
  height: 0;
  border-left: 6px solid transparent;
  border-right: 6px solid transparent;
  border-top: 6px solid #fff;
}

/* Gauge scroll wrapper */
.gauge-scroll-wrapper {
  width: 100%;
  overflow-x: auto;
}

.scroll-inner {
  width: 100%;
}

/* Gauge grid */
.gauge-grid {
  display: flex;
  flex-direction: column;
  width: 100%;
  overflow: visible;
}

.gauge-track {
  position: relative;
  width: 100%;
  height: 13px;
  background: #e0e0e0;
  border-radius: 10px;
  margin-bottom: 24px;
  margin-top: 80px;
  overflow: visible;
}

.gauge-progress {
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  background-color: #CFE5DC;
  border-radius: 10px;
  transition: width 0.5s ease-out;
}

.gauge-checkpoint {
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 28px;
  height: 28px;
  background: white;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
}

.gauge-checkpoint i {
  font-size: 13px;
  color: #aaa;
}

.gauge-checkpoint i.fa-check {
  color: #3b6152;
}

/* Labels row (4 columns) */
.gauge-labels-row {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0;
  width: 100%;
}

.gauge-section-label {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.guage-label {
  font-family: 'Raleway', sans-serif;
  font-weight: 600;
  font-size: 11px;
  color: #2C5E4B;
  text-align: center;
  line-height: 1.3;
  min-height: 28px;
}

.guage-sub-label {
  font-family: 'Raleway', sans-serif;
  font-weight: 300;
  font-size: 9px;
  color: #414141;
  text-align: center;
  line-height: 1.3;
}

.gauge-sub-label {
  font-family: 'Raleway', sans-serif;
  font-weight: 300;
  font-size: 9px;
  color: #414141;
  text-align: center;
  line-height: 1.3;
}

/* Footer */
.confirmation-footer {
  margin-top: 40px;
  border-top: 1px solid #FFFFFF;
  padding-top: 24px;
  color: #000;
}
.confirmation-footer p { margin: 6px 0; }
.btn-link { color: #3B6152; text-decoration: none; text-decoration: underline; }
.btn-link:hover { color: #3B6152; }
.pdf-status-msg { margin-top: 8px; color: #3B6152; }
.pdf-error-msg { margin-top: 8px; color: #b3202a; font-weight: 600; }

/* Responsive */
@media (max-width: 768px) {
  .reservation-details-wrapper { padding: 20px 16px; }
  .stay-summary-inner { flex-direction: column; }
  .summary-dates { width: 100%; flex-wrap: wrap; justify-content: space-between; }
  .summary-item-card { min-width: auto; flex: 1; }
  .extra-info-grid, .lodging-grid { grid-template-columns: 1fr; }
  .help-inner { flex-direction: column; align-items: flex-start; gap: 16px; }
  .help-right { flex-direction: column; border-radius: 16px; padding: 16px 20px; gap: 12px; }
  .privileges-content { grid-template-columns: 1fr; gap: 24px; }
  .privileges-right { max-width: 100%; }
  .total-row { flex-direction: column; align-items: flex-start; gap: 12px; }
  .lodging-summary { flex-direction: column; }
  .lodging-image-wrap { width: 100%; height: 150px; }

  /* Loyalty card mobile */
  .loyalty-card {
    padding: 16px;
    max-width: 100%;
    overflow: visible;
  }
  .loyalty-gauge-container {
    padding-top: 10px;
    overflow: hidden;
  }

  /* Gauge mobile: horizontal scroll */
  .gauge-scroll-wrapper {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    margin: 0 -16px;
    padding: 0 16px;
    max-width: 100%;
  }
  .scroll-inner {
    min-width: 500px;
  }
  .gauge-grid {
    min-width: 500px;
  }
  .gauge-labels-row {
    grid-template-columns: repeat(4, 1fr);
  }
  .guage-label { font-size: 9px; }
  .guage-sub-label,
  .gauge-sub-label { font-size: 9px; }
  
  .bubble-content {
    min-width: 100px;
  }
}

@media (max-width: 480px) {
  .reservation-details-wrapper { padding: 16px 12px; }
  .privileges-content { gap: 16px; }
  .privileges-right { max-width: 100%; }
  
  .loyalty-card {
    padding: 12px;
    max-width: 100%;
  }
  .loyalty-gauge-container {
    padding-top: 10px;
  }
  
  .gauge-scroll-wrapper {
    margin: 0 -12px;
    padding: 0 12px;
    max-width: 100%;
  }
  .gauge-grid {
    min-width: 400px;
  }
  .gauge-labels-row {
    grid-template-columns: repeat(4, 1fr);
  }
  .guage-label { font-size: 10px; }
  .guage-sub-label,
  .gauge-sub-label { font-size: 8px; }
  
  .bubble-content {
    min-width: 90px;
    padding: 5px 8px;
  }
}

.text-green {
  color: #3b6152;
}

/* Status Badge Styles */
.status-badge {
   font-size: 11px;
   font-weight: 700;
   letter-spacing: 0.5px;
   padding: 6px 16px;
   border-radius: 20px;
   display: inline-block;
   text-transform: uppercase;
 }

 .badge-upcoming {
   background: #D56A70;
   color: #fff;
 }

 .badge-past {
   background: #A5D3AE;
   color: #fff;
 }

 .badge-cancelled {
   background: #bbb;
   color: #fff;
 }

 .left-offset-margin {
   margin-left: 7.6rem;
 }

 .pdf-mode {
   width: 100%;
   max-width: none;
   margin: 0 !important;
   padding: 24px;
   box-shadow: none;
   border-radius: 0;
   background: #ffffff;
   overflow: visible;
 }

 .pdf-mode .pdf-exclude {
   display: none !important;
 }

 .pdf-mode .details-section,
 .pdf-mode .stay-summary-card,
 .pdf-mode .reservation-card,
 .pdf-mode .summary-item-card {
   page-break-inside: avoid;
   break-inside: avoid;
 }

 .pdf-mode .stay-summary-inner,
  .pdf-mode .summary-dates,
  .pdf-mode .card-row {
    flex-wrap: nowrap;
  }

/* Supplements details block */
.supplements-details-block {
  border-top: 1px solid #ebebeb;
  padding-top: 16px;
}

.supplements-details-list {
  background: #f7f7f7;
  border-radius: 10px;
  padding: 14px 16px;
  border: 1px solid #e0e0e0;
  margin-top: 10px;
}

.supplements-details-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 0;
  border-bottom: 1px solid #ebebeb;
  font-size: 15px;
  color: #1a1a1a;

}

.supplements-details-row--last {
  border-bottom: none;
}

.supplements-details-total {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 10px;
  margin-top: 6px;
  border-top: 1px solid #bfbfbf;
  color: #1a1a1a;
}
</style>
